import type { RecipeIngredient, StepIngredientMap } from '../types/recipe';
import { cleanIngredientName } from './stepIngredientAliases';
import { parseIngredientText } from './ingredientParser';
import { adjustQuantity, type AdjustOptions } from './adjustRecipeQuantities';
import { classifyRawSectionHeaders } from './normalizeInstructions';
import { groupIngredientsForDisplay, type IngredientGroupInput } from './ingredientGrouping';

const FRACTION_MAP: Record<number, string> = {
  0.125: '⅛',
  0.25: '¼',
  0.333: '⅓',
  0.5: '½',
  0.667: '⅔',
  0.75: '¾',
};

function formatAmount(amount: number): string {
  const whole = Math.floor(amount);
  const decimal = Math.round((amount - whole) * 1000) / 1000;
  const fraction = FRACTION_MAP[decimal];
  if (whole === 0 && fraction) return fraction;
  if (whole > 0 && fraction) return `${whole} ${fraction}`;
  if (Number.isInteger(amount)) return amount.toString();
  return amount.toFixed(1).replace(/\.0$/, '');
}

/**
 * Recipes stored as plain ingredient strings have amount 0 / no unit, but the
 * quantity still sits at the start of `original` ("2 tablespoons safflower or
 * canola oil"). Recover it as display text so chips keep their quantities.
 */
function quantityFromOriginal(ing: RecipeIngredient): string {
  if (!ing.original || !ing.name) return '';
  const cleanOriginal = ing.original.replace(/\s*\(.*?\)/g, ' ').replace(/\s+/g, ' ').trim();
  const idx = cleanOriginal.toLowerCase().indexOf(ing.name.toLowerCase());
  if (idx <= 0) return '';
  return cleanOriginal
    .slice(0, idx)
    .replace(/[,;·]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A chip carries its quantity separately from its name so the UI can mark the
 * quantity alone when we rescaled or converted it.
 */
export interface StepIngredientChip {
  /** Quantity + unit, already adjusted. Empty when the ingredient has none. */
  quantity: string;
  name: string;
  /** True when the quantity differs from what the recipe said. */
  changed: boolean;
  /** The quantity as written, for the reveal. */
  original: string;
  /** Flat "2 tbsp · neutral oil" rendering, for non-marking consumers. */
  text: string;
}

function formatIngredient(ing: RecipeIngredient, opts?: AdjustOptions): StepIngredientChip {
  // Two storage shapes: structured amount+unit, or a quantity still embedded in
  // `original` ("2 tablespoons safflower oil"). Both must adjust identically.
  let numStr = '';
  let unitStr = '';

  if (ing.amount && ing.amount > 0) {
    numStr = formatAmount(ing.amount);
    unitStr = ing.unit ?? '';
  } else {
    const recovered = quantityFromOriginal(ing);
    if (recovered) {
      const parsed = parseIngredientText(`${recovered} ${ing.name}`);
      numStr = parsed.numPart;
      unitStr = parsed.unitPart;
      // Unparseable prefix ("une bonne poignée") — keep it verbatim.
      if (!numStr) {
        return {
          quantity: recovered, name: ing.name, changed: false,
          original: recovered, text: `${recovered} · ${ing.name}`,
        };
      }
    }
  }

  if (!numStr) {
    return { quantity: '', name: ing.name, changed: false, original: '', text: ing.name };
  }

  const adjusted = opts
    ? adjustQuantity(numStr, unitStr, opts)
    : { num: numStr, unit: unitStr, changed: false, original: [numStr, unitStr].filter(Boolean).join(' ') };

  const quantity = [adjusted.num, adjusted.unit].filter(Boolean).join(' ');
  return {
    quantity,
    name: ing.name,
    changed: adjusted.changed,
    original: adjusted.original,
    text: quantity ? `${quantity} · ${ing.name}` : ing.name,
  };
}

/**
 * Words that never identify an ingredient on their own: modifiers, units,
 * recipe filler and process words. They are skipped as single-token candidates
 * so "white wine" in a step never surfaces a "white miso" chip — the full-name
 * candidate ("white miso") still matches when the step really says it.
 * French entries are listed diacritics-stripped (see normalizeForMatching).
 *
 * KNOWN LIMIT — this list cannot converge. It is trying to enumerate every word
 * that is NOT a food word, which is unbounded, while the set that actually
 * matters (words that ARE foods) is finite and already half-built in
 * SEARCHABLE_INGREDIENTS. Every bug filed against step chips so far has been
 * one more word nobody predicted: "sauce", "pâte", "cuisson". Adding the next
 * one buys a week. Matching on a food vocabulary instead of against a blocklist
 * is the fix; see the architecture note in the #581 discussion.
 */
const TOKEN_STOPWORDS = new Set([
  // EN modifiers
  'fresh', 'dried', 'ground', 'minced', 'chopped', 'sliced', 'diced', 'grated',
  'roasted', 'raw', 'whole', 'white', 'black', 'red', 'green', 'yellow',
  'dark', 'light', 'large', 'small', 'medium', 'baby', 'extra', 'virgin',
  'unsweetened', 'neutral', 'full', 'canned', 'packed',
  'thinly', 'finely', 'roughly', 'coarsely',
  // EN units & filler
  'tbsp', 'tsp', 'tablespoon', 'tablespoons', 'teaspoon', 'teaspoons',
  'cup', 'cups', 'gram', 'grams', 'ounce', 'ounces', 'pound', 'pounds', 'lbs',
  'clove', 'cloves', 'pinch', 'dash', 'cans', 'jars', 'bunch', 'bunches',
  'heads', 'stalk', 'stalks', 'sprig', 'sprigs', 'piece', 'pieces',
  'slice', 'slices', 'package', 'packages', 'inch', 'inches',
  'such', 'plus', 'more', 'taste', 'serving', 'servings', 'needed',
  'divided', 'optional', 'about', 'into',
  // Function words. Never a food, and once the head can sit before a trailing
  // prep word (#857) a 3-letter "and" or "for" would otherwise become the head
  // of "jalapeños, seeded and finely chopped" or "Limes, for serving". The
  // 4-letter ones ("pour", "avec", "dans") were already candidates and matched
  // every French step that said « pour ».
  'and', 'for', 'the', 'with', 'from', 'your', 'then', 'that', 'this',
  'et', 'ou', 'pour', 'avec', 'sans', 'dans', 'sur', 'par', 'les', 'des', 'une', 'aux',
  'votre', 'vos', 'leur', 'ses', 'ces',
  // FR modifiers
  'frais', 'fraiche', 'seche', 'sechee', 'moulu', 'moulue', 'hache', 'hachee',
  'haches', 'hachees', 'rape', 'rapee', 'emince', 'emincee', 'cisele', 'ciselee',
  'blanc', 'blanche', 'noir', 'noire', 'rouge', 'vert', 'verte', 'jaune',
  'gros', 'grosse', 'grand', 'grande', 'petit', 'petite', 'moyen', 'moyenne',
  'entier', 'entiere',
  // FR units & filler. « c. à café » and « c. à thé » are the Québec spoons;
  // "cafe" from a raw « 1 ½ cuillères à café de garam masala » pulled the garam
  // masala onto every step that measured salt by the spoon (#857).
  'cuillere', 'cuilleres', 'soupe', 'cafe', 'the', 'tasse', 'tasses', 'gousse', 'gousses',
  'boite', 'boites', 'botte', 'bottes', 'tranche', 'tranches',
  'morceau', 'morceaux', 'pincee', 'environ', 'gout', 'facultatif',
  'portion', 'portions',
  // Process words. They sit inside product names ("crème à cuisson", "pommes
  // à cuire") and steps use them constantly for the act of cooking, so the
  // overlap is guaranteed: "Poursuivre la cuisson" pulled a chip for the
  // cream. Blocking them alone does not generalise — see the note above
  // TOKEN_STOPWORDS on why this list cannot converge.
  'cuisson', 'cuire', 'cuit', 'cuite', 'cuits', 'cuites', 'ebullition',
  'mijoter', 'temperature', 'ambiante', 'cooking', 'baking', 'roasting',
]);

/**
 * French prep participles, diacritics stripped, in every gender and number:
 * hachée(s), émincés, râpée, écrasées, tranchés, épépinés… They end a line the
 * way "minced" does, and a line's head word is the food before them (#857).
 */
const PREP_PARTICIPLE_RE =
  /^(?:(?:hach|emin|cisel|rap|pel|ecras|tranch|epepin|egoutt|rinc|broy|effeuill|coup|grill|fondu|ramolli|tamis|concass|press)ee?s?|cut|cored|torn|drained|rinsed|seeded|stemmed|halved|trimmed|crushed|softened|melted|cubed|shredded|husked|quartered|smashed|beaten|toasted|thawed|squeezed|zested|juiced)$/;

/**
 * Nouns a step uses as readily for the dish being made as for an ingredient
 * being added. "Jusqu'à ce que la sauce soit homogène" is the sauce you are
 * cooking, not the bottle of Worcestershire in the ingredient list — but
 * "ajouter la sauce" in the next recipe really is that bottle.
 *
 * So these are not stopwords. They stay matchable, and `isDishInProgress`
 * decides per step which reading applies. Listed diacritics-stripped.
 */
const DISH_NOUNS = new Set([
  // FR
  'sauce', 'melange', 'preparation', 'appareil', 'pate', 'garniture',
  'bouillon', 'marinade', 'glacage', 'farce', 'croute', 'sirop', 'vinaigrette',
  // EN
  'mixture', 'dough', 'batter', 'filling', 'glaze', 'topping', 'crust',
  'stock', 'broth', 'dressing', 'syrup',
]);

/** "une sauce riche" — an indefinite article introduces a result, not a bottle. */
const INDEFINITE_BEFORE = /(?:^|\s)(?:un|une|a|an)\s+$/;

/** "la sauce soit homogène", "the mixture thickens" — a state, so it is the dish. */
const RESULT_AFTER =
  /^\s+(?:soit|sera|est|devient|devienne|deviendra|epaissi\w*|nappe|napper|reduise|reduit|is|are|becomes?|thickens?|thickened|looks?|coats?)(?:\s|$)/;

/** "jusqu'à l'obtention d'une sauce" — the step names it as the outcome. */
const PRODUCED_BEFORE =
  /(?:obtention|consistance|consistency|texture)\s+(?:d|de|of)?\s*(?:un|une|a|the|la|le)?\s*$/;

/**
 * True when the ingredient has a word of its own that a category noun cannot
 * stand in for: "Worcestershire", "soja", "tomate". Those ingredients must be
 * named — "sauce" alone identifies a shelf, not a bottle. An ingredient with
 * nothing more specific ("Bouillon", "500 ml de bouillon") has only its
 * category noun to match on, so that noun stays usable.
 */
function hasDistinctiveToken(sources: Iterable<string>): boolean {
  for (const source of sources) {
    for (const candidate of candidatesFrom(source)) {
      if (candidate.includes(' ')) continue;
      if (!DISH_NOUNS.has(candidate)) return true;
    }
  }
  return false;
}

/**
 * True when EVERY mention of `token` in the step reads as the dish in progress
 * rather than something being added. One ingredient reading anywhere in the
 * step is enough to keep the chip: "Ajouter la sauce soja, puis cuire jusqu'à
 * ce que la sauce nappe" still deserves its chip.
 */
function isDishInProgress(normalizedStep: string, token: string): boolean {
  const re = new RegExp(`(^|\\s)${escapeRegExp(token)}(?=\\s|$)`, 'g');
  let sawIngredientReading = false;
  let m: RegExpExecArray | null;
  while ((m = re.exec(normalizedStep)) !== null) {
    const start = m.index + m[1].length;
    const before = normalizedStep.slice(0, start);
    const after = normalizedStep.slice(start + token.length);
    const producedHere =
      INDEFINITE_BEFORE.test(before) ||
      PRODUCED_BEFORE.test(before) ||
      RESULT_AFTER.test(after);
    if (!producedHere) sawIngredientReading = true;
    re.lastIndex = start + token.length;
  }
  return !sawIngredientReading;
}

/**
 * Lowercase, strip diacritics, collapse punctuation to spaces. Collapsing
 * punctuation makes French elision matchable for free: "l'oignon" becomes
 * "l oignon", so a word-boundary match on "oignon" succeeds.
 */
function normalizeForMatching(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Ingredient lists and step prose disagree about number constantly: the list
 * says "3 concombre libanais", the step says "coupez les concombres". Matching
 * the exact word left that cucumber off every step of its own recipe. Both
 * languages mark the plural with a trailing s, so match the stem and let the s
 * be optional. Only stems of 4+ characters are folded, which keeps short words
 * where the s is part of the word ("pois", "ail") matching as written.
 */
function pluralTolerant(candidate: string): string {
  const stem =
    candidate.endsWith('s') && candidate.length >= 5 ? candidate.slice(0, -1) : candidate;
  return `${escapeRegExp(stem)}s?`;
}

function hasWordBoundaryMatch(normalizedStep: string, candidate: string): boolean {
  if (!candidate) return false;
  const pattern = candidate.includes(' ')
    ? candidate.split(' ').map(pluralTolerant).join('\\s+')
    : pluralTolerant(candidate);
  return new RegExp(`(^|\\s)${pattern}(\\s|$)`).test(normalizedStep);
}

/**
 * Match candidates for one ingredient string: the full normalized name, then
 * each significant token — length ≥ 4, or the head token down to 3 chars
 * ("oil", "ail") — excluding stopwords and pure numbers. Tokens are what let
 * "neutral oil" match a step that only says "oil".
 *
 * The head is the last token that is not a stopword or a number, not the last
 * token of the string. « 4 gousses d ail hachees » ends on a prep word, and
 * reading the head as "hachees" left "ail" with no candidate at all (#857):
 * the food word a French line ends on is the one before the participle.
 */
function candidatesFrom(source: string): string[] {
  const normalized = normalizeForMatching(source);
  if (!normalized) return [];
  const tokens = normalized.split(' ');
  const candidates = [normalized];
  const isFiller = (token: string) => TOKEN_STOPWORDS.has(token) || /^\d+$/.test(token);
  // A participle is skipped over to find the head, but stays a candidate of
  // its own: « râper les pommes de terre » is still naming the potatoes.
  let head = tokens.length - 1;
  while (head > 0 && (isFiller(tokens[head]) || PREP_PARTICIPLE_RE.test(tokens[head]))) head--;
  tokens.forEach((token, i) => {
    if (isFiller(token)) return;
    if (token.length >= 4 || (i === head && token.length >= 3)) candidates.push(token);
  });
  return candidates;
}

/**
 * "Mélanger tous les ingrédients", "add the remaining ingredients". A step can
 * call for everything without naming anything, and word matching finds nothing
 * to hold on to — the single largest source of missing chips before this rule.
 */
const COLLECTIVE_RE =
  /(?:^|\s)(?:tous|toutes)\s+les\s+(?:autres\s+)?ingredients|les\s+autres\s+ingredients|le\s+reste\s+des\s+ingredients|all\s+(?:the\s+)?(?:remaining\s+)?ingredients|the\s+(?:remaining|rest\s+of\s+the)\s+ingredients/;

/**
 * French steps season by verb, not by noun: "Saler et poivrer" never contains
 * the words "sel" or "poivre", so the salt row went unmatched on every step
 * that called for it.
 */
const SEASONING_RE =
  /(?:^|\s)(?:saler|salez|sale|poivrer|poivrez|poivre|assaisonner|assaisonnez|season|seasoned|salted)(?:\s|$)/;
const SEASONING_INGREDIENT_RE = /\b(sel|salt|poivre|pepper)\b/;

/**
 * The recipe's ingredient sections as index sets, or null when there is nothing
 * to scope by (fewer than two sections, or groups that do not reconcile with
 * the ingredient list).
 *
 * Reuses the assignment the ingredient list already does. Its positional mode
 * matters here: the same salt or oil row often repeats across sections, and
 * matching by name would collapse every copy into whichever section came last.
 */
function ingredientSections(
  allIngredients: RecipeIngredient[],
  groups: IngredientGroupInput[] | undefined,
): Array<{ name: string; indices: number[] }> | null {
  if (!groups?.length) return null;
  const withIndex = allIngredients.map((ing, index) => ({
    index,
    original: ing?.original,
    name: ing?.name,
  }));
  const grouped = groupIngredientsForDisplay(withIndex, groups);
  const sections = grouped.groups
    .map(block => ({ name: block.name, indices: block.items.map(item => item.index) }))
    .filter(section => section.indices.length > 0);
  return sections.length >= 2 ? sections : null;
}

/**
 * True when the step names this section: "mixer tous les ingrédients **de la
 * sauce**". Rare — most collective sentences stop at "ingrédients" — but it is
 * the only signal when a recipe builds its sections out of order.
 */
function matchesQualifier(normalizedStep: string, sectionName: string): boolean {
  const words = normalizeForMatching(sectionName)
    .split(' ')
    .filter(word => word.length >= 4 && !TOKEN_STOPWORDS.has(word));
  if (!words.length) return false;
  return words.some(word => hasWordBoundaryMatch(normalizedStep, word));
}

/** A leading step number, "1. " or "1Dans" / "2 Déposer" (stepNumbering.ts). */
const STORED_KEY_NUMBER_RE = /^\s*\d{1,3}(?:[.)](?!\d)\s*|\s*(?=\p{Lu}))/u;

/**
 * Ingredient rows that are really section labels.
 *
 * Scrapers routinely store "Marinade" or "Crémeux au chocolat" as an ingredient
 * because that is how the source page marks up its groups. Those rows have no
 * quantity and repeat a step header verbatim, and because no step ever *uses*
 * them, the never-hide-an-ingredient rule was promoting them onto every step
 * they shared a word with. A label is not an ingredient; it can never be a chip.
 */
export function sectionLabelRows(stepTexts: string[], ingredients: RecipeIngredient[]): boolean[] {
  const headers = new Set(
    stepTexts
      .map(t => normalizeForMatching(t.replace(/^\s*\d+[.)]\s*/, '')))
      .filter(Boolean),
  );
  if (!headers.size) return ingredients.map(() => false);
  return ingredients.map(ing => {
    const line = ing?.original || ing?.name || '';
    if (!line || /\d/.test(line)) return false; // a quantity means a real ingredient
    const normalized = normalizeForMatching(line.replace(/\s*\(.*?\)/g, ''));
    return Boolean(normalized) && headers.has(normalized);
  });
}

/**
 * How well a step identifies an ingredient.
 *
 * `weak` is the interesting one: the step mentions the ingredient's category
 * ("sauce") but nothing that pins it to this bottle. On its own that is the
 * #581 bug. Across a whole recipe it is useful evidence — it is the only
 * mention this ingredient will ever get, or it is not.
 */
type MatchStrength = 'strong' | 'weak' | 'none';

function matchStrength(normalizedStep: string, ing: RecipeIngredient): MatchStrength {
  // Candidates come from the cleaned name, the cleaned original (covers
  // language mismatches: name "tomatoes", step "tomates") AND the raw
  // original — commas normalize to spaces there, so names the comma-strip
  // shortens ("unsweetened, full-fat canned coconut milk") still contribute
  // their real tokens (coconut, milk).
  const sources = new Set<string>();
  if (ing.name) sources.add(ing.name);
  if (ing.original) {
    sources.add(cleanIngredientName(ing.original));
    sources.add(ing.original);
  }

  // A category noun standing alone ("sauce" for "sauce Worcestershire") is the
  // one weak candidate: it names a whole shelf, and steps use those words for
  // the dish they are building. Collect them and rule on them last, once every
  // stronger candidate has had its chance.
  const dishNounHits: string[] = [];
  for (const source of sources) {
    for (const candidate of candidatesFrom(source)) {
      if (!hasWordBoundaryMatch(normalizedStep, candidate)) continue;
      if (candidate.includes(' ') || !DISH_NOUNS.has(candidate)) return 'strong';
      dishNounHits.push(candidate);
    }
  }
  if (!dishNounHits.length) return 'none';
  // "Sauce" cannot pin down "sauce Worcestershire" — only the recipe as a whole
  // can say whether this mention is all we have.
  if (hasDistinctiveToken(sources)) return 'weak';
  // Nothing more specific exists, so the category noun is the ingredient's only
  // handle. It still loses to a step that is plainly describing the dish.
  return dishNounHits.some(token => !isDishInProgress(normalizedStep, token)) ? 'strong' : 'weak';
}

/**
 * Chips for any ingredient mentioned in the step text — including shortened
 * mentions ("oil" for "neutral oil"). One chip per ingredient, recipe order
 * preserved.
 *
 * Pass `opts` to have quantities follow the servings ratio and the selected
 * measurement system. Without it the quantities render exactly as stored.
 */
export function resolveStepIngredientChips(
  stepText: string,
  allIngredients: RecipeIngredient[],
  opts?: AdjustOptions,
): StepIngredientChip[] {
  if (!stepText) return [];
  return resolveRecipeStepChips([stepText], allIngredients, opts)[0] ?? [];
}

/**
 * Chips for every step of a recipe at once.
 *
 * Resolving the whole recipe is what lets the matcher be strict without ever
 * costing the cook an ingredient. A bare category noun is refused as an
 * identifier ("sauce" is not enough to mean the Worcestershire) — but only
 * while some other step names that ingredient outright. If no step does, its
 * weak mentions are promoted rather than dropped, because an ingredient that
 * appears on no step at all is one the cook can miss.
 *
 * Callers that resolve a single step get the permissive reading for the same
 * reason: with one step there is no elsewhere for an ingredient to appear.
 */
export function resolveRecipeStepChips(
  stepTexts: string[],
  allIngredients: RecipeIngredient[],
  opts?: AdjustOptions,
  stored?: StepIngredientMap[],
  ingredientGroups?: IngredientGroupInput[],
): StepIngredientChip[][] {
  return resolveRecipeStepMatches(stepTexts, allIngredients, stored, ingredientGroups).map(indices =>
    indices.map(i => formatIngredient(allIngredients[i], opts)),
  );
}

/**
 * The matching decision on its own: which ingredients each step uses, as
 * indices into `allIngredients`.
 *
 * Separated from chip formatting so accuracy can be scored against labelled
 * fixtures without going through display strings, which are lossy (two
 * ingredients can render the same label) and change whenever the unit toggle
 * or the servings ratio does. See `npm run eval:step-ingredients`.
 */
export function resolveRecipeStepMatches(
  stepTexts: string[],
  allIngredients: RecipeIngredient[],
  stored?: StepIngredientMap[],
  ingredientGroups?: IngredientGroupInput[],
): number[][] {
  if (!stepTexts?.length || !allIngredients?.length) {
    return (stepTexts ?? []).map(() => []);
  }

  // A stored map is the answer resolved once at import, and scores far better
  // than anything word matching reaches. It is consulted per step: a step the
  // map does not cover (a re-scrape reworded it, a partial backfill) falls back
  // on its own rather than discarding the whole map.
  const inRange = (indices: number[]) =>
    indices.filter(i => Number.isInteger(i) && i >= 0 && i < allIngredients.length);

  // Keyed without the publisher's step number: the map was stored against the
  // raw text ("1Dans un bol…") and the screen asks with the cleaned one
  // ("Dans un bol…"), or the other way round once the data is repaired (#794).
  const storedKey = (text: string) => normalizeForMatching(text.replace(STORED_KEY_NUMBER_RE, ''));
  const storedByStep = new Map<string, number[]>();
  for (const entry of stored ?? []) {
    const key = storedKey(entry?.step || '');
    if (!key || !Array.isArray(entry.ingredients)) continue;
    storedByStep.set(key, inRange(entry.ingredients));
  }

  // Text is the primary key, but it is not stable across languages: a recipe
  // shown in a language it was not written in has every step rewritten by
  // recipeTranslationService, which keeps the list length and the order and
  // changes only the words. So when the lengths line up, position is a sound
  // second key — without it the map silently stops applying for exactly the
  // French-recipe-in-English case it was built for.
  const positional =
    stored?.length === stepTexts.length
      ? stored.map(entry => (Array.isArray(entry?.ingredients) ? inRange(entry.ingredients) : null))
      : null;

  // A section header is a label, not something a cook does, so nothing attaches
  // to it. Recipes that store their headers as steps ("Marinade", "Crémeux au
  // chocolat") were otherwise collecting a chip for every ingredient whose name
  // the header happened to repeat.
  const headerSteps = classifyRawSectionHeaders(stepTexts);
  const isLabelRow = sectionLabelRows(stepTexts, allIngredients);

  const normalizedSteps = stepTexts.map(normalizeForMatching);
  const strengths = normalizedSteps.map((normalizedStep, s) => {
    const seasons = !headerSteps[s] && SEASONING_RE.test(normalizedStep);
    return allIngredients.map((ing, i) => {
      if (!ing?.name || isLabelRow[i]) return 'none' as MatchStrength;
      if (seasons && SEASONING_INGREDIENT_RE.test(normalizeForMatching(ing.original || ing.name))) {
        return 'strong' as MatchStrength;
      }
      const strength = matchStrength(normalizedStep, ing);
      // A header is a label, so it cannot claim an ingredient outright. It can
      // still be the only place one is named — "Sauce" as a heading, never
      // repeated in the steps below — so demote rather than discard, and let
      // the never-hide rule promote it back if nothing else claims it.
      if (headerSteps[s] && strength === 'strong') return 'weak' as MatchStrength;
      return strength;
    });
  });

  // "Tous les ingrédients" means one part of the recipe, not the whole list.
  // Needs the other steps resolved first, so it runs as a second pass.
  const sections = ingredientSections(allIngredients, ingredientGroups);
  normalizedSteps.forEach((normalizedStep, s) => {
    if (headerSteps[s] || !COLLECTIVE_RE.test(normalizedStep)) return;

    const claimedEarlier = (i: number) =>
      strengths.some((row, other) => other < s && row[i] === 'strong');
    const claimable = (i: number) => Boolean(allIngredients[i]?.name) && !isLabelRow[i];

    if (sections) {
      // A sectioned recipe is cooked section by section, so the phrase means
      // the earliest section still to be used — not everything left in the
      // recipe. Without this, a collective in step 0 claimed all 25 rows of a
      // three-part recipe, and the later step that said "tous les ingrédients
      // de la sauce" was then left with nothing.
      const target =
        sections.find(sec => matchesQualifier(normalizedStep, sec.name)) ??
        sections.find(sec => sec.indices.some(i => claimable(i) && !claimedEarlier(i)));
      if (!target) return;
      const unclaimed = target.indices.filter(i => claimable(i) && !claimedEarlier(i));
      // Every row of the section is already spoken for — the phrase is looking
      // back at what it built, so it adds nothing.
      (unclaimed.length ? unclaimed : []).forEach(i => {
        strengths[s][i] = 'strong';
      });
      return;
    }

    // No sections to scope by: fall back to everything the earlier steps have
    // not claimed. Right for a single-part recipe, and there is no better
    // signal available for a multi-part one that never recorded its groups.
    allIngredients.forEach((_, i) => {
      if (claimable(i) && !claimedEarlier(i)) strengths[s][i] = 'strong';
    });
  });

  const promoted = allIngredients.map(
    (_, i) => !strengths.some(row => row[i] === 'strong'),
  );

  const matched = strengths.map(row => {
    // Dedup on id, but only when there is one. Recipes imported as raw JSON can
    // reach us with every id undefined, and a Set keyed on that collapses the
    // whole list into a single chip.
    const seen = new Set<number | string>();
    const matches: number[] = [];
    allIngredients.forEach((ing, i) => {
      const key = ing?.id ?? `idx:${i}`;
      if (!ing?.name || seen.has(key)) return;
      if (row[i] === 'strong' || (row[i] === 'weak' && promoted[i])) {
        seen.add(key);
        matches.push(i);
      }
    });
    return matches;
  });

  const resolved = stepTexts.map(
    (text, s) => storedByStep.get(storedKey(text)) ?? positional?.[s] ?? matched[s],
  );

  // The stored map is more precise than matching, and it is right to exclude
  // what a step says to leave out ("sauf les oignons verts"). But it answers one
  // step at a time and never sees the recipe as a whole, so an ingredient
  // excluded everywhere ends up on no step at all. Never-hide outranks the map:
  // anything the map drops entirely comes back where matching would have put it.
  const onSomeStep = new Set(resolved.flat());
  const stranded = allIngredients
    .map((ing, i) => i)
    .filter(i => allIngredients[i]?.name && !isLabelRow[i] && !onSomeStep.has(i));
  if (!stranded.length) return resolved;

  const rescued = resolved.map(step => [...step]);
  for (const i of stranded) {
    const target = matched.findIndex(step => step.includes(i));
    if (target < 0) continue; // matching could not place it either
    rescued[target] = [...rescued[target], i].sort((a, b) => a - b);
  }
  return rescued;
}

/**
 * Ingredients the recipe lists that end up on no step at all — the ones a
 * cook working step by step will never be shown (#857, « ail »).
 *
 * The never-hide pass above can only rescue an ingredient that some step
 * mentions at least weakly; one that produces no candidate anywhere falls
 * straight through, and until #857 nothing recorded that it had. Screens
 * report this set (`useStepChipOrphans`) so the next blind spot is found in
 * PostHog rather than in a shake report.
 */
export function unplacedIngredients(
  stepTexts: string[],
  allIngredients: RecipeIngredient[],
  stored?: StepIngredientMap[],
  ingredientGroups?: IngredientGroupInput[],
): number[] {
  if (!stepTexts?.length || !allIngredients?.length) return [];
  const placed = new Set(
    resolveRecipeStepMatches(stepTexts, allIngredients, stored, ingredientGroups).flat(),
  );
  const isLabelRow = sectionLabelRows(stepTexts, allIngredients);
  return allIngredients
    .map((_, i) => i)
    .filter(i => Boolean(allIngredients[i]?.name) && !isLabelRow[i] && !placed.has(i));
}

/**
 * Flat-string form (e.g. "2 tbsp · neutral oil"), for consumers that cannot
 * mark individual quantities.
 */
export function resolveStepIngredients(
  stepText: string,
  allIngredients: RecipeIngredient[],
  opts?: AdjustOptions,
): string[] {
  return resolveStepIngredientChips(stepText, allIngredients, opts).map(c => c.text);
}

/** Flat-string form of {@link resolveRecipeStepChips}, one array per step. */
export function resolveRecipeStepIngredients(
  stepTexts: string[],
  allIngredients: RecipeIngredient[],
  opts?: AdjustOptions,
  stored?: StepIngredientMap[],
  ingredientGroups?: IngredientGroupInput[],
): string[][] {
  return resolveRecipeStepChips(stepTexts, allIngredients, opts, stored, ingredientGroups).map(chips =>
    chips.map(c => c.text),
  );
}
